import React from 'react';
import styles from './Styles/EventOps.module.css';

const CENTRAL_TZ = 'America/Chicago';
const fmtCentral = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        timeZone: CENTRAL_TZ,
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '';

/**
 * Reachability (SCRUM-356), four states — a device id alone is not proof of
 * reachability, only that a check might confirm it:
 *  - 'push'    — pushValid === true (confirmed live device) -> green ●
 *  - 'unknown' — a device id on file but never checked (pushValid == null)
 *                -> grey ● ("not checked" must not read as verified)
 *  - 'sms'     — pushValid === false (confirmed dead) but a phone on file
 *                -> ◐
 *  - 'none'    — no device (or a dead one) and no phone -> ○
 */
export function reachStatus({ pushValid, oneSignalID, hasPhone }) {
  if (pushValid === true) return 'push';
  if (oneSignalID && pushValid == null) return 'unknown';
  if (pushValid === false && hasPhone) return 'sms';
  return 'none';
}

/**
 * Maps the campaign-audience endpoint's own precomputed `reachability`
 * ('push'|'sms'|'none', from api's campaign-audience.ts) plus the parent's
 * `pushValid` onto the four-state vocabulary above. The audience endpoint's
 * 'push' does not distinguish a confirmed-live device from a
 * never-checked one — pushValid does: 'push' + pushValid == null -> 'unknown'.
 * 'sms' and 'none' from the audience endpoint already match this module's
 * 'sms'/'none' 1:1 (both are keyed off pushValid === false there too), so
 * they pass through unchanged.
 */
export function reachStatusFromAudience(reachability, pushValid) {
  if (reachability === 'push') return pushValid == null ? 'unknown' : 'push';
  return reachability;
}

const GLYPH = { push: '●', unknown: '●', sms: '◐', none: '○' };
const CSS_CLASS = { push: 'reachOk', unknown: 'reachUnknown', sms: 'reachSms', none: 'reachNone' };
const LABEL = { push: 'push', unknown: 'unchecked', sms: 'sms only', none: 'unreachable' };

export function reachLabel(status) {
  return LABEL[status] ?? LABEL.none;
}

function reachTitle(status, pushCheckedAt) {
  if (status === 'push') {
    return pushCheckedAt ? `push ok · checked ${fmtCentral(pushCheckedAt)}` : 'push ok';
  }
  if (status === 'unknown') return 'push device on file — not checked';
  if (status === 'sms') return 'push device dead — SMS only';
  return 'unreachable';
}

/**
 * Renders ●/●/◐/○ for a parent's reachability.
 *
 * Two call shapes:
 *  - Raw fields `{ pushValid, oneSignalID, hasPhone, pushCheckedAt }` — used
 *    by the ops roster/profile panel, which only has the parent's raw
 *    push/phone fields.
 *  - A precomputed `{ status, pushCheckedAt }` — used by AudienceBuilder via
 *    `reachStatusFromAudience`, since the campaign-audience endpoint already
 *    resolves most of this server-side.
 */
export default function ReachBadge({ pushValid, oneSignalID, hasPhone, status, pushCheckedAt }) {
  const resolved = status ?? reachStatus({ pushValid, oneSignalID, hasPhone });
  return (
    <span
      className={`${styles.reachBadge} ${styles[CSS_CLASS[resolved]] ?? ''}`}
      title={reachTitle(resolved, pushCheckedAt)}
    >
      {GLYPH[resolved] ?? GLYPH.none}
    </span>
  );
}
